import {
  uploadPrivateImageApi
} from 'api';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import { AnyAction } from 'redux';


const MAX_CTA_FILE_SIZE = 250 * 1024;

interface ImageDimensions {
  Desktop: { width: number; height: number };
  Mobile: { width: number; height: number };
}

interface ImageUploadConfig {
  dispatch: React.Dispatch<AnyAction>
  files: FileList;
  value: string;
  imageType: ImageType;
  ownImagesTagIds: string;
  storeCode: string;
  desktopTagData: IAssetCategoryTag | undefined;
  mobileTagData: IAssetCategoryTag | undefined;
  privateTagData: IAssetCategoryTag | undefined;
  currentCategoryId: number | null;
}

export const IMAGE_DIMENSIONS: Record<string, ImageDimensions> = {
  vertical: {
    Desktop: { width: 2025, height: 491 },
    Mobile: { width: 380, height: 552 }
  },
  horizontal_top: {
    Desktop: { width: 983, height: 491 },
    Mobile: { width: 380, height: 552 }
  },
  horizontal_bottom: {
    Desktop: { width: 983, height: 491 },
    Mobile: { width: 380, height: 552 }
  }
};

export const TAG_CODES: Record<string, { desktop: string; mobile: string }> = {
  vertical: {
    desktop: 'block_1_body_desktop_lite',
    mobile: 'block_1_body_mobile_lite'
  },
  horizontal_top: {
    desktop: 'block_2_face_desktop_lite',
    mobile: 'block_2_face_mobile_lite'
  },
  horizontal_bottom: {
    desktop: 'block_3_skin_desktop_lite',
    mobile: 'block_3_skin_mobile_lite'
  }
};

export const validateImageFile = (
  file: File,
  fileType: string,
  imageType: ImageType,
  dimensions: { width: number; height: number },
  dispatch: React.Dispatch<AnyAction>
): Promise<boolean> => {
  return new Promise((resolve) => {
    const fileTypeDotIndexPosition = fileType.lastIndexOf('.') + 1;
    const slicedFileTypeFromFilePath = fileType.slice(fileTypeDotIndexPosition);
    const acceptableFormats = 'png|jpeg';
    const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const image = new Image();
      image.src = e.target?.result as string;

      image.onload = () => {
        const { height, width } = image;

        if (!hasImageFile.test(file.type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
          dispatch(uploadFileTypeUnsupported());
          resolve(false);
        } else if (width !== dimensions.width || height !== dimensions.height) {
          dispatch(uploadImageResolutionLimit(`${dimensions.width} x ${dimensions.height}px`));
          resolve(false);
        } else if (file.size > MAX_CTA_FILE_SIZE) {
          dispatch(uploadFileSizeLimit('250kB'));
          resolve(false);
        } else {
          resolve(true);
        }
      };

      image.onerror = () => {
        dispatch(uploadFileTypeUnsupported());
        resolve(false);
      };
    };

    reader.onerror = () => {
      dispatch(uploadFileTypeUnsupported());
      resolve(false);
    };
  });
};

export const uploadImage = async (
  config: ImageUploadConfig,
  blockType: 'vertical' | 'horizontal_top' | 'horizontal_bottom'
): Promise<AssetImage | null> => {
  const {
    dispatch,
    files,
    value,
    imageType,
    ownImagesTagIds,
    storeCode,
    desktopTagData,
    mobileTagData,
    privateTagData,
    currentCategoryId
  } = config;

  if (!files.length) {
    return null;
  }

  const [file] = Array.from(files);
  const dimensions = IMAGE_DIMENSIONS[blockType][imageType];

  const isValid = await validateImageFile(file, value, imageType, dimensions, dispatch);

  if (!isValid)  {
    return null;
  }

  const formData = new FormData();
  formData.append('image', file);

  if (ownImagesTagIds.length) {
    const tagCodes = TAG_CODES[blockType];
    const tags = imageType === 'Desktop'
      ? `${tagCodes.desktop},private,${storeCode}`
      : `${tagCodes.mobile},private,${storeCode}`;
    formData.append('tags', tags);
  } else {
    const tagByResolutionType = imageType === 'Desktop'
      ? desktopTagData?.tagCode
      : mobileTagData?.tagCode;

    if (!tagByResolutionType || !privateTagData) {
      return null;
    }

    const tags = [
      tagByResolutionType,
      privateTagData.tagCode,
      {
        label: storeCode,
        tagCode: storeCode,
        category: currentCategoryId
      }
    ];
    formData.append('tags', JSON.stringify(tags));
  }

  const uploadedData = await uploadPrivateImageApi({
    dispatch,
    formData,
    isAddingNewTag: !ownImagesTagIds.length,
    categoryName: 'Storebuild-automaton'
  });

  return uploadedData || null;
};

export const buildTagIds = (
  tagData1: IAssetCategoryTag | undefined,
  tagData2: IAssetCategoryTag | undefined,
  tagData3: IAssetCategoryTag | undefined
): string => {
  if (tagData1 && tagData2 && tagData3) {
    return `${tagData1.id}, ${tagData2.id}, ${tagData3.id}`;
  }

  return '';
};
